/* ===== assets/custom-cart-discount.js ===== */
(() => {
  const ROOT = window.Shopify?.routes?.root || '/';

  const OPEN_UTILITY_KEY = 'cart_open_utility';
  const DISCOUNT_ERROR_KEY = 'cart_discount_error';
  const DISCOUNT_ERROR_TIMEOUT = 10000;

  const CART_EVENTS = [
    'cart:refresh',
    'cart:change',
    'cart:updated'
  ];

  const normalizeCode = (value) =>
    String(value || '').trim().toLowerCase();

  const safeSessionSet = (key, value) => {
    try {
      sessionStorage.setItem(key, value);
    } catch (error) {}
  };

  const safeSessionGet = (key) => {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  };

  const safeSessionRemove = (key) => {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {}
  };

  window.addEventListener('pagehide', () => {
    safeSessionRemove(DISCOUNT_ERROR_KEY);
  });

  const rememberDiscountPanel = () => {
    safeSessionSet(OPEN_UTILITY_KEY, 'discount');
  };

  const saveDiscountError = (message) => {
    safeSessionSet(
      DISCOUNT_ERROR_KEY,
      String(message || '')
    );
  };

  const clearSavedDiscountError = () => {
    safeSessionRemove(DISCOUNT_ERROR_KEY);
  };

  const isCartPage = () => {
    const pageType =
      window.themeVariables?.settings?.pageType;

    const path =
      window.location.pathname.replace(/\/+$/, '');

    return pageType === 'cart' || path === '/cart';
  };

  const fetchCart = async () => {
    try {
      const response = await fetch(`${ROOT}cart.js`, {
        headers: {
          Accept: 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('cart_fetch_failed');
      }

      return await response.json();
    } catch (error) {
      return {};
    }
  };

  if (!window.__cartPromise) {
    window.__cartPromise = fetchCart();
  }

  const setCurrentCart = (cart) => {
    window.__cartPromise = cart
      ? Promise.resolve(cart)
      : fetchCart();
  };

  CART_EVENTS.forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      if (event?.detail?.cart) {
        setCurrentCart(event.detail.cart);
      }
    });
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      setCurrentCart(null);
    }
  });

  const dispatchCartEvents = (cart) => {
    setCurrentCart(cart);

    document.dispatchEvent(
      new CustomEvent('cart:refresh', {
        detail: { cart }
      })
    );

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: { cart }
      })
    );
  };

  const dispatchDiscountError = (message) => {
    const resolvedMessage =
      message ||
      'Rabatkoden kunne ikke anvendes.';

    saveDiscountError(resolvedMessage);

    document.dispatchEvent(
      new CustomEvent('cart:discount:error', {
        detail: {
          message: resolvedMessage
        }
      })
    );
  };

  const dispatchDiscountSuccess = () => {
    clearSavedDiscountError();

    document.dispatchEvent(
      new CustomEvent('cart:discount:success')
    );
  };

  const openDiscountPanel = (element) => {
    const shell = element?.closest?.(
      '[data-cart-utilities-shell]'
    );

    if (!shell) {
      return;
    }

    const trigger = shell.querySelector(
      '[data-cart-utility-trigger][data-target="discount"]'
    );

    const card = shell.querySelector(
      '[data-cart-utility="discount"]'
    );

    const panel = shell.querySelector(
      '[data-cart-utility-panel="discount"]'
    );

    if (trigger) {
      trigger.setAttribute(
        'aria-expanded',
        'true'
      );
    }

    if (card) {
      card.classList.add('is-open');
    }

    if (panel) {
      panel.hidden = false;
      panel.classList.add('is-open');
    }
  };

  class AbstractCartDiscount extends HTMLElement {
    async getCart() {
      const currentCart =
        await window.__cartPromise;

      if (
        currentCart &&
        Object.keys(currentCart).length > 0
      ) {
        return currentCart;
      }

      const freshCart = await fetchCart();

      setCurrentCart(freshCart);

      return freshCart;
    }

    async getAppliedCodes() {
      const cart = await this.getCart();

      const discounts =
        Array.isArray(cart?.discount_codes)
          ? cart.discount_codes
          : [];

      return discounts
        .filter((discount) => {
          if (typeof discount === 'string') {
            return true;
          }

          return discount?.applicable !== false;
        })
        .map((discount) => {
          if (typeof discount === 'string') {
            return discount;
          }

          return (
            discount?.code ||
            discount?.title ||
            ''
          );
        })
        .map(normalizeCode)
        .filter(Boolean)
        .filter((code, index, codes) =>
          codes.indexOf(code) === index
        );
    }

    async postDiscount(
      discountCsv,
      submittedCode = ''
    ) {
      const response = await fetch(
        `${ROOT}cart/update.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            discount: String(discountCsv || '')
          })
        }
      );

      let cart;

      try {
        cart = await response.json();
      } catch (error) {
        throw new Error('invalid_response');
      }

      if (!response.ok) {
        throw new Error(
          cart?.description ||
          cart?.message ||
          'update_failed'
        );
      }

      if (submittedCode) {
        const normalizedSubmittedCode =
          normalizeCode(submittedCode);

        const returnedDiscounts =
          Array.isArray(cart?.discount_codes)
            ? cart.discount_codes
            : [];

        const submittedDiscount =
          returnedDiscounts.find((discount) => {
            const returnedCode =
              typeof discount === 'string'
                ? discount
                : (
                  discount?.code ||
                  discount?.title ||
                  ''
                );

            return (
              normalizeCode(returnedCode) ===
              normalizedSubmittedCode
            );
          });

        const wasApplied =
          typeof submittedDiscount === 'string' ||
          submittedDiscount?.applicable === true;

        if (!wasApplied) {
          const errorMessage =
            'Rabatkoden kunne ikke anvendes. Kontrollér, at koden er korrekt, og at betingelserne for rabatten er opfyldt.';

          saveDiscountError(errorMessage);
          dispatchCartEvents(cart);

          const error =
            new Error('invalid_code');

          error.cart = cart;

          throw error;
        }
      }

      clearSavedDiscountError();
      dispatchCartEvents(cart);

      return cart;
    }

    reloadIfCartPage() {
      if (isCartPage()) {
        window.location.reload();
      }
    }

    showErrorBanner(message) {
      rememberDiscountPanel();
      openDiscountPanel(this);
      dispatchDiscountError(message);
    }

    hideErrorBanner() {
      dispatchDiscountSuccess();
    }
  }

  class CartDiscountBanner extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') {
        return;
      }

      this.dataset.initialized = 'true';

      this._abortController =
        new AbortController();

      this._hideTimer = null;

      const signal =
        this._abortController.signal;

      const clearHideTimer = () => {
        if (this._hideTimer) {
          window.clearTimeout(this._hideTimer);
          this._hideTimer = null;
        }
      };

      const hideBanner = () => {
        clearHideTimer();
        this.hidden = true;
        clearSavedDiscountError();
      };

      const scheduleAutoHide = () => {
        clearHideTimer();

        this._hideTimer = window.setTimeout(() => {
          hideBanner();
        }, DISCOUNT_ERROR_TIMEOUT);
      };

      const ensureCloseButton = () => {
        let closeButton = this.querySelector(
          '[data-cart-discount-error-close]'
        );

        if (closeButton) {
          return closeButton;
        }

        const content =
          this.querySelector('.cart-discount__error') ||
          this.firstElementChild ||
          this;

        closeButton = document.createElement('button');
        closeButton.type = 'button';
        closeButton.className =
          'cart-discount__error-close';
        closeButton.setAttribute(
          'data-cart-discount-error-close',
          ''
        );
        closeButton.setAttribute(
          'aria-label',
          'Luk fejlmeddelelse'
        );
        closeButton.innerHTML =
          '<span aria-hidden="true">&times;</span>';

        closeButton.addEventListener(
          'click',
          hideBanner,
          { signal }
        );

        content.appendChild(closeButton);

        return closeButton;
      };

      const showMessage = (message) => {
        const messageElement =
          this.querySelector(
            '[data-cart-discount-error-message]'
          );

        if (messageElement && message) {
          messageElement.textContent = message;
        }

        ensureCloseButton();

        this.hidden = false;
        scheduleAutoHide();
      };

      const savedMessage =
        safeSessionGet(DISCOUNT_ERROR_KEY);

      if (savedMessage) {
        window.requestAnimationFrame(() => {
          showMessage(savedMessage);
        });
      }

      document.addEventListener(
        'cart:discount:error',
        (event) => {
          showMessage(
            event?.detail?.message ||
            'Rabatkoden kunne ikke anvendes.'
          );
        },
        { signal }
      );

      document.addEventListener(
        'cart:discount:success',
        () => {
          hideBanner();
        },
        { signal }
      );
    }

    disconnectedCallback() {
      if (this._hideTimer) {
        window.clearTimeout(this._hideTimer);
        this._hideTimer = null;
      }

      this._abortController?.abort();
    }
  }

  class CartDiscountField
    extends AbstractCartDiscount {
    connectedCallback() {
      if (this.dataset.initialized === 'true') {
        return;
      }

      this.dataset.initialized = 'true';

      this._hidden =
        this.querySelector(
          'input[type="hidden"][name="discount"]'
        );

      this._input =
        this.querySelector(
          'input:not([type="hidden"])'
        );

      this._loader =
        this.querySelector(
          '.cart-discount-field__loader'
        );

      this._originalHiddenValue =
        this._hidden?.value || '';

      this._applying = false;

      this._abortController =
        new AbortController();

      const signal =
        this._abortController.signal;

      if (!this._input) {
        return;
      }

      this._input.addEventListener(
        'input',
        () => {
          this.hideErrorBanner();

          if (!this._hidden) {
            return;
          }

          const typed =
            this._input.value.trim();

          const originalCodes =
            this._originalHiddenValue
              .split(',')
              .map((code) => code.trim())
              .filter(Boolean);

          const combinedCodes =
            typed
              ? [...originalCodes, typed]
              : originalCodes;

          this._hidden.value =
            combinedCodes.join(',');
        },
        { signal }
      );

      this._input.addEventListener(
        'change',
        () => {
          this.applyCode();
        },
        { signal }
      );

      this._input.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Enter') {
            return;
          }

          event.preventDefault();
          event.stopPropagation();

          this.applyCode();
        },
        { signal }
      );
    }

    disconnectedCallback() {
      this._abortController?.abort();
    }

    setLoading(state) {
      if (this._loader) {
        this._loader.hidden = !state;
      }

      if (this._input) {
        this._input.disabled = state;
      }

      this.setAttribute(
        'aria-busy',
        state ? 'true' : 'false'
      );
    }

    async applyCode() {
      if (this._applying) {
        return;
      }

      const inputValue =
        String(this._input?.value || '').trim();

      if (!inputValue) {
        return;
      }

      this._applying = true;

      rememberDiscountPanel();
      openDiscountPanel(this);

      this.setLoading(true);
      this.hideErrorBanner();

      try {
        const appliedCodes =
          await this.getAppliedCodes();

        const normalizedNewCode =
          normalizeCode(inputValue);

        if (
          appliedCodes.includes(
            normalizedNewCode
          )
        ) {
          this.showErrorBanner(
            'Rabatkoden er allerede anvendt.'
          );

          return;
        }

        const nextCodes = [
          ...appliedCodes,
          normalizedNewCode
        ];

        await this.postDiscount(
          nextCodes.join(','),
          normalizedNewCode
        );

        if (this._input) {
          this._input.value = '';
        }

        if (this._hidden) {
          this._hidden.value =
            nextCodes.join(',');
        }

        this.hideErrorBanner();
        rememberDiscountPanel();

        this.reloadIfCartPage();
      } catch (error) {
        const message =
          error?.message === 'invalid_code'
            ? 'Rabatkoden kunne ikke anvendes. Kontrollér, at koden er korrekt, og at betingelserne for rabatten er opfyldt.'
            : 'Der opstod en fejl ved anvendelse af rabatkoden. Prøv igen.';

        this.showErrorBanner(message);
      } finally {
        this._applying = false;
        this.setLoading(false);
      }
    }
  }

  class CartDiscountRemoveButton
    extends AbstractCartDiscount {
    connectedCallback() {
      if (this.dataset.initialized === 'true') {
        return;
      }

      this.dataset.initialized = 'true';

      this._abortController =
        new AbortController();

      this.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopPropagation();

          this.removeCode();
        },
        {
          signal:
            this._abortController.signal
        }
      );
    }

    disconnectedCallback() {
      this._abortController?.abort();
    }

    setBusy(state) {
      this.setAttribute(
        'aria-busy',
        state ? 'true' : 'false'
      );

      const button =
        this.querySelector(
          'button, [role="button"]'
        );

      if (button) {
        button.disabled = state;
      }
    }

    async removeCode() {
      if (
        this.getAttribute('aria-busy') ===
        'true'
      ) {
        return;
      }

      const codeToRemove =
        normalizeCode(
          this.getAttribute(
            'discount-code'
          )
        );

      if (!codeToRemove) {
        return;
      }

      this.setBusy(true);
      this.hideErrorBanner();
      rememberDiscountPanel();

      try {
        const appliedCodes =
          await this.getAppliedCodes();

        const remainingCodes =
          appliedCodes.filter(
            (code) =>
              code !== codeToRemove
          );

        await this.postDiscount(
          remainingCodes.join(',')
        );

        rememberDiscountPanel();
        this.reloadIfCartPage();
      } catch (error) {
        this.showErrorBanner(
          'Rabatkoden kunne ikke fjernes. Prøv igen.'
        );
      } finally {
        this.setBusy(false);
      }
    }
  }

  class CartDiscountClearAllButton
    extends AbstractCartDiscount {
    connectedCallback() {
      if (this.dataset.initialized === 'true') {
        return;
      }

      this.dataset.initialized = 'true';

      this._abortController =
        new AbortController();

      this.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopPropagation();

          this.clearAll();
        },
        {
          signal:
            this._abortController.signal
        }
      );
    }

    disconnectedCallback() {
      this._abortController?.abort();
    }

    setBusy(state) {
      this.setAttribute(
        'aria-busy',
        state ? 'true' : 'false'
      );

      const button =
        this.querySelector(
          'button, [role="button"]'
        );

      if (button) {
        button.disabled = state;
      }
    }

    async clearAll() {
      if (
        this.getAttribute('aria-busy') ===
        'true'
      ) {
        return;
      }

      this.setBusy(true);
      this.hideErrorBanner();
      rememberDiscountPanel();

      try {
        await this.postDiscount('');

        rememberDiscountPanel();
        this.reloadIfCartPage();
      } catch (error) {
        this.showErrorBanner(
          'Rabatkoderne kunne ikke fjernes. Prøv igen.'
        );
      } finally {
        this.setBusy(false);
      }
    }
  }

  if (
    !customElements.get(
      'cart-discount-field'
    )
  ) {
    customElements.define(
      'cart-discount-field',
      CartDiscountField
    );
  }

  if (
    !customElements.get(
      'cart-discount-remove-button'
    )
  ) {
    customElements.define(
      'cart-discount-remove-button',
      CartDiscountRemoveButton
    );
  }

  if (
    !customElements.get(
      'cart-discount-clear-all'
    )
  ) {
    customElements.define(
      'cart-discount-clear-all',
      CartDiscountClearAllButton
    );
  }

  if (
    !customElements.get(
      'cart-discount-banner'
    )
  ) {
    customElements.define(
      'cart-discount-banner',
      CartDiscountBanner
    );
  }

  /*
   * Gendan kun bannertekst efter dynamiske section-renderinger.
   * Panelet åbnes kun, hvis cart_open_utility stadig er "discount".
   * Dermed kan brugeren lukke rabatfeltet og åbne Contribe.
   */
  if (
    !window.__cartDiscountRestoreObserver &&
    typeof MutationObserver !== 'undefined'
  ) {
    let restoreScheduled = false;

    window.__cartDiscountRestoreObserver =
      new MutationObserver(() => {
        if (restoreScheduled) {
          return;
        }

        restoreScheduled = true;

        window.requestAnimationFrame(() => {
          restoreScheduled = false;

          const shouldOpen =
            safeSessionGet(OPEN_UTILITY_KEY) ===
            'discount';

          const savedError =
            safeSessionGet(DISCOUNT_ERROR_KEY);

          if (savedError) {
            document
              .querySelectorAll(
                'cart-discount-banner'
              )
              .forEach((banner) => {
                const messageElement =
                  banner.querySelector(
                    '[data-cart-discount-error-message]'
                  );

                if (messageElement) {
                  messageElement.textContent =
                    savedError;
                }

                banner.hidden = false;
              });
          }

          if (shouldOpen) {
            const field =
              document.querySelector(
                'cart-discount-field'
              );

            if (field) {
              openDiscountPanel(field);
            }
          }
        });
      });

    window.__cartDiscountRestoreObserver.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );
  }
  if (
    !document.getElementById(
      'custom-cart-discount-error-styles'
    )
  ) {
    const style = document.createElement('style');

    style.id =
      'custom-cart-discount-error-styles';

    style.textContent = `
      cart-discount-banner .cart-discount__error {
        position: relative;
        padding-right: 42px;
      }

      .cart-discount__error-close {
        position: absolute;
        top: 8px;
        right: 8px;
        width: 28px;
        height: 28px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        border: 0;
        border-radius: 999px;
        background: transparent;
        color: currentColor;
        font: inherit;
        font-size: 22px;
        line-height: 1;
        cursor: pointer;
        opacity: .72;
      }

      .cart-discount__error-close:hover {
        opacity: 1;
        background: rgba(0, 0, 0, .06);
      }

      .cart-discount__error-close:focus-visible {
        outline: 2px solid currentColor;
        outline-offset: 2px;
      }
    `;

    document.head.appendChild(style);
  }

})();
